import * as Sentry from "@sentry/browser";
import { ActionLoadUser } from "@store/user";
import { ActionLogoutUser } from "../store/user/login/login-actions";
import { AppState } from "@store/state";
import { BasePage } from "./base";
import { Component, Injector, ViewEncapsulation } from "@angular/core";
import { ConnectionService } from "ng-connection-service";
import { MenuController, Platform } from "@ionic/angular";
import { PosQuote } from "../models";
import { Router } from "@angular/router";
import { skipWhile, take } from "rxjs/operators";
import { SplashScreen } from "@ionic-native/splash-screen/ngx";
import { StatusBar } from "@ionic-native/status-bar/ngx";
import { StorageService } from "src/providers";
import { Store as AppStore } from "@ngrx/store";
import { AngularFirestore } from "@angular/fire/firestore";

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent extends BasePage {
  constructor(
    private platform: Platform,
    private splashScreen: SplashScreen,
    private statusBar: StatusBar,
    public injector: Injector,
    public store: AppStore<AppState>,
    public menuCtrl: MenuController,
    private router: Router,
    public storage: StorageService,
    public networkConnection: ConnectionService,
    public afsStore: AngularFirestore
  ) {
    super(injector);
    this.initializeApp();
    this.monitorInternetStatus();
  }

  /**

   * monitor the browser. If user is offline show overlay popup.
   * comeback to online dismiss the popup. 
   */
  monitorInternetStatus() {
    // check for when user reload the webpage
    const status = navigator.onLine;
    if (!status) {
      this.internetPopoverAlert();
    }

    window.addEventListener("offline", (e) => {
      this.internetPopoverAlert();
    });

    window.addEventListener("online", (e) => {
      this.alertCtrl.dismiss();
      location.reload();
    });
  }

  /**
   * Determins the browser is private browser or not
   * but its storage quota size
   * if quota < 120MB - private browser
   * works only for the chrome under 55 and firefox under 57
   */
  async findIncognito() {
    const { usage, quota } = await (<any>navigator).storage.estimate();
    if (quota < 120000000) {
      alert(
        "Pos is not supported in private mode. Please contact tech support"
      );
      window.open("https://www.google.com", "_parent");
    }
  }

  /**
   * Enable Firebase presistence 
   * will work the application in offline mode also.
   */
  setFirebasePresistence() {
    const firestore = this.afsStore.firestore;
    firestore.enablePersistence({ synchronizeTabs: true }).catch((error) => {
      if (error.code == 'failed-precondition') {
        alert(
          "Pos already opened in another tab. please use the single tab"
        );
        window.open("https://www.google.com", "_parent");
      }
      if (error.code == "unimplemented") {
        alert(
          "Pos not supported. Please try in some other browser"
        );
        window.open("https://www.google.com", "_parent");
      }
    });
  }

  initializeApp() {
    this.setFirebasePresistence();
    this.findIncognito();
    this.platform.ready().then(() => {
      this.statusBar.styleDefault();
      this.splashScreen.hide();
      this.store.dispatch(new ActionLoadUser("pos_user"));

      this.store
        .select((state) => state.userState)
        .pipe(
          skipWhile((userState) =>
            userState ? userState.loading == true : true
          ),
          take(1)
        )
        .subscribe((userState) => {
          if (userState.user == null) {
            this.router.navigate(["login"]);
            return;
          }
          Sentry.configureScope((scope) => {
            scope.setUser({
              id: userState.user.userId,
              username: userState.user.username,
              phone: userState.user.phone,
            });
          });
          this.router.navigate(["home"]);
        });
    });
  }

  /**when cart is not cleared
   *  should not allow to logout
   *  if cart is clear it allow to logout
   */
  async logout() {
    this.menuCtrl.close();
    const quote = await this.storage
      .getData("cart_quote", PosQuote)
      .toPromise();
    if (quote.cartProducts.length > 0) {
      this.showAlert("Cannot Logout", "Please clear the cart");
      return;
    }

    this.store.dispatch(new ActionLogoutUser());
    this.router.navigate(["login"]);
  }

  openOrderHistory() {
    this.menuCtrl.close();
    this.router.navigate(["orders"]);
  }

  openOOSProducts() {
    this.menuCtrl.close();
    this.router.navigate(["oos"]);
  }

  checkout() {
    this.menuCtrl.close();
    this.router.navigate(["home"]);
  }

  openOrderDetails() {
    this.menuCtrl.close();
    this.router.navigate(["carry-bag"]);
  }

  /**used to route the page to online orders */
  openPickupOrders() {
    this.menuCtrl.close();
    this.router.navigate(["pickup-orders"]);
  }
}
